import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ContractorAuthenticationModal from '../../../../components/family-plaza/ContractorAuthenticationModal';
import PortraitPhotoRegistrationModal from '../../../../components/family-plaza/PortraitPhotoRegistrationModal';
import MemorialAlbumEditModal from '../../../../components/family-plaza/MemorialAlbumEditModal';
import { checkModalAuthenticationStatus } from '../../../../utils/authUtils';
import { getCurrentUserProfilePhoto, getCurrentCustomerName, setupProfilePhotoListener, getDeceasedPersonDate, getDeceasedPersonProfileText, incrementAlbumViewCount, getAlbumViewCount } from '../../../../utils/profilePhotoUtils';
import CyberMemorialService from '../../../../services/FamilyPlaza/CyberMemorialService';
import DeceasedPersonProfile from '../../../../components/family-plaza/DeceasedPersonProfile';
import FamilyPlazaTopSection from '../../../../components/family-plaza/FamilyPlazaTopSection';
import FamilyPlazaSubHeading from '../../../../components/family-plaza/FamilyPlazaSubHeading';


const MemorialAlbumArticleDetailsMobile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [memorialAlbumData, setMemorialAlbumData] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [pendingModal, setPendingModal] = useState(null); // Track which modal to open after auth
  const [profilePhotoUrl, setProfilePhotoUrl] = useState('/mobile donate/group-129.png');
  const [password, setPassword] = useState('');
  const [isPasswordCorrect, setIsPasswordCorrect] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [enteredPassword, setEnteredPassword] = useState('');
  const [showPasswordError, setShowPasswordError] = useState(false);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleWriteAlbum = () => {
    navigate('/memorial-album-writing');
  };

  const handleWriteButton = () => {
    navigate('/memorial-album-writing');
  };

  const handleNavigation = (route) => {
    navigate(route);
  };

  const handleMemorialPost = () => {
    navigate('/memorial-post-board-list');
  };

  const handleLetterToHeaven = () => {
    navigate('/letter-to-heaven-board-list');
  };

  const handleManagementInquiry = () => {
    navigate('/management-search');
  };

  const handleAncestralTablet = () => {
    navigate('/ancestral-tablet');
  };

  // Load album data from navigation state (primary method)
  useEffect(() => {
    const loadAlbumData = () => {
      // Try to get album data from navigation state first
      if (location.state && location.state.albumData) {
        setMemorialAlbumData(location.state.albumData);
      } else {
        // Fallback to localStorage if no navigation state
        const savedData = localStorage.getItem('memorialAlbumData');
        if (savedData) {
          try {
            const parsedData = JSON.parse(savedData);
            setMemorialAlbumData(parsedData);
          } catch (error) {
            console.error('Error parsing album data:', error);
          }
        }
      }
    };
    loadAlbumData();
  }, [location.state]);

  // Increment view count when page loads
  useEffect(() => {
    if (memorialAlbumData && memorialAlbumData.id) {
      incrementAlbumViewCount(memorialAlbumData.id, memorialAlbumData.readCount || 0);
    }
  }, [memorialAlbumData]);

  // Load profile photo and listen for updates
  useEffect(() => {


    // Set up profile photo listener
    const customerName = getCurrentCustomerName();
    const cleanup = setupProfilePhotoListener(setProfilePhotoUrl, customerName);

    return cleanup;
  }, []);

  // Get current user name for display
  const getCurrentUserName = () => {
    const selectedPersonData = localStorage.getItem('selectedPersonData');
    if (selectedPersonData) {
      try {
        const personData = JSON.parse(selectedPersonData);
        return personData.customerName || '이추모';
      } catch (error) {
        return '이추모';
      }
    }
    return '이추모';
  };

  // Handle password verification
  const handlePasswordSubmit = (passwordToCheck) => {
    const password = passwordToCheck || enteredPassword;

    if (memorialAlbumData && password === memorialAlbumData.password) {
      setIsPasswordCorrect(true);
      setShowPasswordError(false);
    } else {
      setIsPasswordCorrect(false);
      setShowPasswordError(true);
    }
  };

  // Photo icon click handler
  const handlePhotoIconClick = () => {
    const isAuthenticated = checkModalAuthenticationStatus();

    if (isAuthenticated) {
      // If already authenticated, open photo modal directly
      setShowPhotoModal(true);
    } else {
      // If not authenticated, open auth modal first
      setPendingModal('photo');
      setShowAuthModal(true);
    }
  };

  // Authentication modal handlers
  const handleAuthConfirm = () => {
    setShowAuthModal(false);
    // Open the pending modal after successful authentication
    if (pendingModal === 'photo') {
      setShowPhotoModal(true);
    } else if (pendingModal === 'edit') {
      setShowEditModal(true);
    } else if (pendingModal === 'delete') {
      handleDeleteConfirm();
    }
    setPendingModal(null);
  };

  const handleAuthClose = () => {
    setShowAuthModal(false);
    setPendingModal(null);
  };

  // Photo registration modal handlers
  const handlePhotoConfirm = () => {
    setShowPhotoModal(false);
  };

  const handlePhotoUpload = (photoUrl) => {
    setProfilePhotoUrl(photoUrl);
  };

  const handlePhotoClose = () => {
    setShowPhotoModal(false);
  };

  // Edit modal handlers
  const handleEditClick = () => {
    const isAuthenticated = checkModalAuthenticationStatus();

    if (isAuthenticated) {
      // If already authenticated, open edit modal directly
      setShowEditModal(true);
    } else {
      // If not authenticated, open auth modal first
      setPendingModal('edit');
      setShowAuthModal(true);
    }
  };

  const handleEditConfirm = (updatedData) => {
    // Update the current album data with the response from the API
    if (updatedData) {
      const updatedAlbumData = {
        ...memorialAlbumData,
        title: updatedData.albumTitle,
        image: updatedData.photoUrl || updatedData.photoThumbUrl,
        photoThumbUrl: updatedData.photoThumbUrl,
        registTime: updatedData.registTime,
        readCount: updatedData.readCount
      };
      setMemorialAlbumData(updatedAlbumData);
    }
    setShowEditModal(false);
  };

  const handleEditClose = () => {
    setShowEditModal(false);
  };

  // Delete functionality
  const handleDeleteClick = () => {
    const isAuthenticated = checkModalAuthenticationStatus();

    if (isAuthenticated) {
      // If already authenticated, show delete confirmation
      handleDeleteConfirm();
    } else {
      // If not authenticated, open auth modal first
      setPendingModal('delete');
      setShowAuthModal(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!memorialAlbumData || !memorialAlbumData.albumId) {
      console.error('No album data or albumId available for deletion');
      return;
    }

    // Show confirmation dialog
    const confirmed = window.confirm('정말로 이 추모앨범을 삭제하시겠습니까?\n삭제된 앨범은 복구할 수 없습니다.');

    if (!confirmed) {
      return;
    }

    try {
      const response = await CyberMemorialService.deleteMemorialAlbumById(memorialAlbumData.albumId);

      if (response) {
        // Show success message
        alert('앨범이 성공적으로 삭제되었습니다.');

        // Navigate back to the album list
        navigate('/memorial-album-board-list');
      }
    } catch (error) {
      console.error('Error deleting memorial album:', error);
      alert('앨범 삭제에 실패했습니다. 다시 시도해주세요.');
    }
  };

  // Handle action buttons
  const handleListButton = () => {
    navigate('/memorial-album-board-list');
  };

  return (
    <div className="memorial-album-article-details-mobile-unique announcement-list-page cyber-info-page-pc">

      <FamilyPlazaTopSection />

      <main className="main-content">

        <FamilyPlazaSubHeading heading="사이버추모관" />


        <DeceasedPersonProfile
          handlePhotoIconClick={handlePhotoIconClick}
        />


        {/* Navigation Tabs */}
        <div className="memorial-album-article-details-mobile-nav-tabs-unique">
          <button
            className="memorial-album-article-details-mobile-nav-tab-unique"
            onClick={() => navigate('/donate')}
          >
            <span>헌화하기</span>
          </button>
          <button
            className="memorial-album-article-details-mobile-nav-tab-unique"
            onClick={() => navigate('/memorial-post-board-list')}
          >
            <span>추모글</span>
          </button>
          <button
            className="memorial-album-article-details-mobile-nav-tab-unique memorial-album-article-details-mobile-nav-tab-active-unique"
            onClick={() => navigate('/memorial-album-board-list')}
          >
            <span>추모앨범</span>
          </button>
        </div>

        {/* Memorial Album Article Details Content */}
        <div className="memorial-album-article-details-mobile-content-unique">
          {/* Post Header */}
          <div className="memorial-album-article-details-mobile-post-header-unique">
            {/* <div className="memorial-album-article-details-mobile-view-count-unique">
               👁 {memorialAlbumData ? getAlbumViewCount(memorialAlbumData.id, memorialAlbumData.readCount || 0) : 0}
             </div> */}
            <div className="memorial-album-article-details-mobile-view-count-unique">
              <svg
                stroke="currentColor"
                fill="currentColor"
                strokeWidth="0"
                viewBox="0 0 576 512"
                height="1em"
                width="1em"
                xmlns="http://www.w3.org/2000/svg"
                style={{ marginRight: '4px', verticalAlign: 'middle' }}
              >
                <path d="M572.52 241.4C518.29 135.59 410.93 64 288 64S57.68 135.64 3.48 241.41a32.35 32.35 0 0 0 0 29.19C57.71 376.41 165.07 448 288 448s230.32-71.64 284.52-177.41a32.35 32.35 0 0 0 0-29.19zM288 400a144 144 0 1 1 144-144 143.93 143.93 0 0 1-144 144zm0-240a95.31 95.31 0 0 0-25.31 3.79 47.85 47.85 0 0 1-66.9 66.9A95.78 95.78 0 1 0 288 160z"></path>
              </svg>
              {memorialAlbumData ? getAlbumViewCount(memorialAlbumData.id, memorialAlbumData.readCount || 0) : 0}
            </div>

            <div className="memorial-album-article-details-mobile-post-title-unique" style={{ textAlign: 'left' }}>
              {memorialAlbumData ? memorialAlbumData.title : 'No Title'}
            </div>
            <div className="memorial-album-article-details-mobile-sender-info-unique" style={{ textAlign: 'right' }}>
              From {getCurrentUserName()} | {memorialAlbumData ? memorialAlbumData.date : 'No Date'}
            </div>
          </div>

          {/* Main Image */}
          <div className="memorial-album-article-details-mobile-main-image-unique">
            <img
              src={memorialAlbumData ? memorialAlbumData.image : '/images/album1.png'}
              alt={memorialAlbumData ? memorialAlbumData.title : 'Album Image'}
            />
          </div>





          {/* Action Buttons */}
          <div className="memorial-album-article-details-mobile-action-buttons-unique">
            <button
              className="memorial-album-article-details-mobile-list-button-unique"
              onClick={(e) => {
                e.preventDefault();
                handleListButton();
              }}
            >
              목록
            </button>
            <button
              className="memorial-album-article-details-mobile-modify-button-unique"
              onClick={(e) => {
                e.preventDefault();
                handleEditClick();
              }}
            >
              수정
            </button>
            <button
              className="memorial-album-article-details-mobile-delete-button-unique"
              onClick={(e) => {
                e.preventDefault();
                handleDeleteClick();
              }}
            >
              삭제
            </button>
          </div>
        </div>

        {/* Authentication Modal */}
        <ContractorAuthenticationModal
          isOpen={showAuthModal}
          onConfirm={handleAuthConfirm}
          onClose={handleAuthClose}
        />

        {/* Photo Registration Modal */}
        <PortraitPhotoRegistrationModal
          isOpen={showPhotoModal}
          onConfirm={handlePhotoConfirm}
          onClose={handlePhotoClose}
          onPhotoUpload={handlePhotoUpload}
        />

        {/* Memorial Album Edit Modal */}
        <MemorialAlbumEditModal
          isOpen={showEditModal}
          onConfirm={handleEditConfirm}
          onClose={handleEditClose}
          albumData={memorialAlbumData}
        />
      </main>
    </div>
  );
};

export default MemorialAlbumArticleDetailsMobile; 
